import { Controller } from "@hotwired/stimulus"

// Shared native-dialog behavior. Layout, color and animation belong to feature CSS.
const scrollOwners = new Set()
let previousOverflow

export default class extends Controller {
  static targets = ["panel", "trigger"]
  static values = { autoOpen: Boolean, persistentMedia: String, removeOnCache: Boolean, resetFormOnOpen: Boolean }

  get dialog() { return this.hasPanelTarget ? this.panelTarget : this.element }
  get initialReturnFocus() { return null }
  get persistent() { return Boolean(this.media?.matches) }

  connect() {
    this.returnFocus = this.initialReturnFocus
    this.syncMedia = this.syncMedia.bind(this)
    if (this.hasPersistentMediaValue) {
      this.media = matchMedia(this.persistentMediaValue)
      this.media.addEventListener("change", this.syncMedia)
      this.syncMedia()
    } else if (this.autoOpenValue) {
      this.open()
    }
  }

  open(event) {
    if (this.persistent || this.dialog.open) return
    this.returnFocus = event?.currentTarget || this.returnFocus || document.activeElement
    if (this.resetFormOnOpenValue) this.dialog.querySelector("form")?.reset()
    this.dispatch("opening", { prefix: "dialog" })
    this.dialog.showModal()
    this.setExpanded(true)
    if (scrollOwners.size === 0) previousOverflow = document.documentElement.style.overflow
    scrollOwners.add(this)
    document.documentElement.style.overflow = "hidden"
  }

  close() {
    if (this.persistent) return
    if (this.dialog.open) this.dialog.close()
    this.closed()
  }

  closed() {
    // Ignore delayed native close events if the same dialog has already reopened.
    if (this.dialog.open) return
    this.setExpanded(false)
    this.restoreScroll()
    if (this.returnFocus?.isConnected !== false) this.returnFocus?.focus({ preventScroll: true })
  }

  backdrop(event) {
    if (event.target !== this.dialog || this.persistent) return
    const rect = this.dialog.getBoundingClientRect()
    if (event.clientX < rect.left || event.clientX > rect.right ||
        event.clientY < rect.top || event.clientY > rect.bottom) this.close()
  }

  otherOpened(event) {
    if (event.target !== this.element) this.close()
  }

  syncMedia() {
    const focusInside = this.dialog.contains(document.activeElement)
    if (this.dialog.open) this.dialog.close()
    this.restoreScroll()
    this.setExpanded(false)
    if (this.persistent) {
      // Non-modal desktop panels must not steal focus on every Turbo visit.
      this.dialog.open = true
    } else if (focusInside && this.hasTriggerTarget) {
      this.triggerTarget.focus({ preventScroll: true })
    }
  }

  discard() {
    this.returnFocus = null
    if (this.dialog.open) this.dialog.close()
    this.setExpanded(false)
    this.restoreScroll()
    if (this.removeOnCacheValue) this.element.remove()
  }

  disconnect() {
    this.media?.removeEventListener("change", this.syncMedia)
    this.discard()
  }

  setExpanded(open) {
    if (this.hasTriggerTarget) this.triggerTarget.setAttribute("aria-expanded", String(open))
  }

  restoreScroll() {
    if (!scrollOwners.delete(this)) return
    if (scrollOwners.size === 0) document.documentElement.style.overflow = previousOverflow
  }
}
